import {
  createAnimatedComponent,
  type SharedValue,
  useAnimatedProps,
} from "@-/com.npmjs.react-native-reanimated";
import { Circle, G, Line, Path, Svg, Text as SvgText } from "@-/com.npmjs.react-native-svg";
import {
  CHINA_MAP_LABEL_FONT_SIZE,
  getChinaMapLabelOffsetUnit,
} from "@-/dev.improve.haniverse.data.china-map";
import {
  finiteSvgNumber,
  finiteSvgViewBox,
  svgDivisor,
} from "@-/dev.improve.haniverse.lib.svg-geometry";
import { CHINA_MAP_LOCATION_MARKER } from "#china-map-location-marker";
import {
  CHINA_MAP_LABEL_BASELINE_OFFSET,
  getChinaMapProvinceColors,
  getChinaMapStyledLabelGlyphs,
  getChinaMapProvinceRegions,
  isChinaMapRegionShaded,
  type ChinaMapLabel,
} from "#china-map-places";
import {
  getChinaMapCityLabelZoomOpacity,
  getChinaMapLabelZoomPictureScale,
} from "#china-map-label-zoom";
import type { ChinaMapRendererProps } from "#china-map-renderer-props";
import type { ChinaMapColors } from "#use-china-map-colors";
import { useChinaMapFirstFrame } from "#use-china-map-first-frame";

const AnimatedSvg = createAnimatedComponent(Svg);

/** The browser paints the committed SVG at the next frame. */
const isPaintedByNextFrame = () => true;
const AnimatedGroup = createAnimatedComponent(G);
const AnimatedLine = createAnimatedComponent(Line);

/** Browser SVG uses the browser's vector renderer and system fonts without loading CanvasKit. */
export function ChinaMapRenderer({
  cityLabelProvince,
  colors,
  focusedProvince,
  labels,
  labelScale,
  onFirstFrame,
  originX,
  originY,
  provinces,
  scale,
  travel,
  viewport,
}: ChinaMapRendererProps) {
  useChinaMapFirstFrame(isPaintedByNextFrame, onFirstFrame);
  // Only `viewBox`: Reanimated sets each animated prop as an attribute on the browser's <svg>, so
  // react-native-svg's native viewBox fields (`minX`, `vbWidth`, …) would be unknown DOM props.
  const animatedProps = useAnimatedProps(() => {
    const currentScale = svgDivisor(finiteSvgNumber(scale.get(), 1));
    const box = finiteSvgViewBox(
      originX.get(),
      originY.get(),
      Math.max(1, viewport.width) / currentScale,
      Math.max(1, viewport.height) / currentScale,
    );
    return { viewBox: box.viewBox };
  });
  const traveller =
    travel.route === null
      ? travel.current
      : ([
          travel.route.from[0] +
            (travel.route.to[0] - travel.route.from[0]) * travel.route.progress,
          travel.route.from[1] +
            (travel.route.to[1] - travel.route.from[1]) * travel.route.progress,
        ] as const);
  const [markerX, markerY] = traveller ?? [0, 0];
  // The marker keeps its screen size at every zoom, centered on the learner.
  const markerProps = useAnimatedProps(() => ({
    transform: `translate(${markerX} ${markerY}) scale(${
      1 / svgDivisor(finiteSvgNumber(scale.get(), 1))
    })`,
  }));
  return (
    // Hidden from assistive tech like the native Skia map, whose drawing exposes nothing. React
    // Native's `accessible` would reach the browser's <svg> as an invalid attribute.
    <AnimatedSvg
      animatedProps={animatedProps}
      aria-hidden
      height={viewport.height}
      width={viewport.width}
    >
      {provinces.map((province) => {
        const provinceColors = getChinaMapProvinceColors(province, focusedProvince, colors);
        return getChinaMapProvinceRegions(province).map((child) => (
          <Path
            d={child.place.path}
            fill={
              isChinaMapRegionShaded(province, child, cityLabelProvince)
                ? provinceColors.unlockedLand
                : provinceColors.land
            }
            key={child.place.adcode}
            stroke={provinceColors.cityBorder}
            strokeLinejoin="round"
            strokeWidth={0.75}
            vectorEffect="non-scaling-stroke"
          />
        ));
      })}
      {focusedProvince?.children.map((child) => (
        <Path
          d={child.place.path}
          fill="none"
          key={child.place.adcode}
          stroke={colors.focusedCityBorder}
          strokeLinejoin="round"
          strokeWidth={0.75}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {provinces.map((province) => (
        <Path
          d={province.place.path}
          fill="none"
          key={province.place.adcode}
          stroke={getChinaMapProvinceColors(province, focusedProvince, colors).provinceBorder}
          strokeLinejoin="round"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {/* Redraw the focused edge last so a neighboring muted edge cannot cover it. */}
      {focusedProvince === null ? null : (
        <Path
          d={focusedProvince.place.path}
          fill="none"
          stroke={colors.provinceBorder}
          strokeLinejoin="round"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {travel.route === null ? null : (
        <G pointerEvents="none">
          <Line
            opacity={0.35}
            stroke={colors.route}
            strokeLinecap="round"
            strokeWidth={2.5}
            vectorEffect="non-scaling-stroke"
            x1={travel.route.from[0]}
            x2={travel.route.to[0]}
            y1={travel.route.from[1]}
            y2={travel.route.to[1]}
          />
          {traveller === null ? null : (
            <Line
              stroke={colors.route}
              strokeLinecap="round"
              strokeWidth={2.5}
              vectorEffect="non-scaling-stroke"
              x1={travel.route.from[0]}
              x2={traveller[0]}
              y1={travel.route.from[1]}
              y2={traveller[1]}
            />
          )}
        </G>
      )}
      {/* Beneath the names, so the marker never hides one; its place keeps its own label. */}
      {traveller === null ? null : (
        <AnimatedGroup animatedProps={markerProps} pointerEvents="none">
          <Circle
            cx={0}
            cy={0}
            fill={colors.markerOutline}
            r={CHINA_MAP_LOCATION_MARKER.radius + CHINA_MAP_LOCATION_MARKER.ringWidth}
          />
          <Circle cx={0} cy={0} fill={colors.marker} r={CHINA_MAP_LOCATION_MARKER.radius} />
        </AnimatedGroup>
      )}
      {labels.map((label) => (
        <PlaceLabel
          colors={colors}
          // Only city names are left out where they are too small.
          fadesOut={cityLabelProvince !== null}
          // A new layout mounts with sizes for the camera as it is, rather than drawing a frame
          // at the previous layout's.
          key={`${label.view.place.adcode}:${label.layout.em}`}
          label={label}
          labelScale={labelScale}
          scale={scale}
        />
      ))}
    </AnimatedSvg>
  );
}

/**
 * A name as it was laid out when the camera last came to rest, sized (and, for a city, faded) for
 * the live zoom, so it follows a pinch or a flight frame by frame (see `#china-map-label-zoom`).
 */
function PlaceLabel({
  colors,
  fadesOut,
  label: { fontSize, layout, leader, view },
  labelScale,
  scale,
}: {
  colors: ChinaMapColors;
  fadesOut: boolean;
  label: ChinaMapLabel;
  labelScale: number;
  scale: SharedValue<number>;
}) {
  const { em } = layout;
  const [anchorX, anchorY] = layout.anchor;
  const [toX, toY] = leader?.to ?? layout.anchor;
  const labelProps = useAnimatedProps(() => ({
    opacity: fadesOut
      ? getChinaMapCityLabelZoomOpacity(em, svgDivisor(finiteSvgNumber(scale.get(), 1)))
      : 1,
  }));
  // Glyphs are set at the full font size around their spot, then scaled onto the map.
  const glyphProps = useAnimatedProps(() => ({
    transform: `scale(${getChinaMapLabelZoomPictureScale(
      em,
      svgDivisor(finiteSvgNumber(scale.get(), 1)),
    )})`,
  }));
  // The name is drawn larger or smaller on the map as the zoom changes, so a callout line's end
  // moves with the name's edge.
  const leaderProps = useAnimatedProps(() => {
    // How many times larger on the map the name is now than when it was laid out.
    const growth =
      getChinaMapLabelZoomPictureScale(em, svgDivisor(finiteSvgNumber(scale.get(), 1))) /
      svgDivisor(getChinaMapLabelZoomPictureScale(em, svgDivisor(labelScale)));
    return { x2: anchorX + (toX - anchorX) * growth, y2: anchorY + (toY - anchorY) * growth };
  });
  const glyphs = getChinaMapStyledLabelGlyphs(
    view,
    layout.offsets,
    leader === undefined
      ? colors
      : { label: colors.calloutLabel, lockedLabel: colors.calloutLabel },
  );
  // Scattered characters are pinned to their own spots on the map; the others share the anchor.
  const unit = getChinaMapLabelOffsetUnit(layout, fontSize / labelScale);
  const spots =
    layout.isScattered === true
      ? glyphs.map((glyph) => ({
          glyphs: [{ ...glyph, x: 0, y: 0 }],
          x: anchorX + glyph.x * unit,
          y: anchorY + glyph.y * unit,
        }))
      : [{ glyphs, x: anchorX, y: anchorY }];
  return (
    <AnimatedGroup animatedProps={labelProps} pointerEvents="none">
      {leader === undefined ? null : (
        <AnimatedLine
          animatedProps={leaderProps}
          stroke={colors.calloutLabel}
          strokeLinecap="round"
          strokeWidth={0.75}
          vectorEffect="non-scaling-stroke"
          x1={leader.from[0]}
          y1={leader.from[1]}
        />
      )}
      {spots.map((spot, spotIndex) => (
        // eslint-disable-next-line react/no-array-index-key -- a name's characters never reorder
        <G key={spotIndex} transform={`translate(${spot.x} ${spot.y})`}>
          <AnimatedGroup animatedProps={glyphProps}>
            {spot.glyphs.map(({ color, fontWeight, text, x, y }, index) => (
              <SvgText
                fill={color}
                fontSize={CHINA_MAP_LABEL_FONT_SIZE}
                fontWeight={fontWeight}
                // eslint-disable-next-line react/no-array-index-key -- a name's characters never reorder
                key={index}
                textAnchor="middle"
                x={x * CHINA_MAP_LABEL_FONT_SIZE}
                y={CHINA_MAP_LABEL_BASELINE_OFFSET + y * CHINA_MAP_LABEL_FONT_SIZE}
              >
                {text}
              </SvgText>
            ))}
          </AnimatedGroup>
        </G>
      ))}
    </AnimatedGroup>
  );
}
